import React from "react";
import { TextElement } from "../../types";

interface TextEditingControlsProps {
  selectedText: TextElement;
  onTextUpdate: (id: string, updates: Partial<TextElement>) => void;
}

const TextEditingControls: React.FC<TextEditingControlsProps> = ({
  selectedText,
  onTextUpdate,
}) => {
  return (
    <div className="border-t border-gray-200 pt-3 mb-3">
      <h4
        className="font-bold text-gray-800 mb-2 text-center text-sm"
        dir="rtl"
      >
        עריכת טקסט
      </h4>
      <div className="space-y-2">
        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            טקסט
          </label>
          <input
            type="text"
            value={selectedText.text}
            onChange={(e) =>
              onTextUpdate(selectedText.id, { text: e.target.value })
            }
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-pink-500 focus:border-transparent text-center"
            dir="rtl"
          />
        </div>

        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            גופן
          </label>
          <select
            value={selectedText.fontFamily}
            onChange={(e) =>
              onTextUpdate(selectedText.id, { fontFamily: e.target.value })
            }
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-pink-500 focus:border-transparent"
            dir="rtl"
          >
            <option value="Arial">Arial</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Georgia">Georgia</option>
            <option value="Verdana">Verdana</option>
            <option value="Franklin Gothic Medium">
              Franklin Gothic Medium
            </option>
            <option value="Impact">Impact</option>
            <option value="Comic Sans MS">Comic Sans MS</option>
            <option value="David">David (Hebrew)</option>
            <option value="Miriam">Miriam (Hebrew)</option>
            <option value="Noto Sans Hebrew">Noto Sans Hebrew</option>
            <option value="Heebo">Heebo (Hebrew)</option>
          </select>
        </div>

        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            גודל גופן: {Math.round(selectedText.fontSize * 100)}%
          </label>
          <input
            type="range"
            min="0.05"
            max="0.3"
            step="0.01"
            value={selectedText.fontSize}
            onChange={(e) =>
              onTextUpdate(selectedText.id, {
                fontSize: parseFloat(e.target.value),
              })
            }
            className="w-full h-1 bg-gray-200 rounded appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #ec4899 0%, #eab308 100%)`,
            }}
          />
        </div>

        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            כיוון כתיבה
          </label>
          <div className="flex gap-1">
            <button
              dir="rtl"
              onClick={() => onTextUpdate(selectedText.id, { dir: "rtl" })}
              className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                selectedText.dir === "rtl"
                  ? "bg-pink-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              RTL
            </button>
            <button
              dir="rtl"
              onClick={() => onTextUpdate(selectedText.id, { dir: "ltr" })}
              className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                selectedText.dir === "ltr"
                  ? "bg-pink-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              LTR
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label
              className="block text-xs font-medium text-gray-700 mb-1"
              dir="rtl"
            >
              צבע טקסט
            </label>
            <input
              type="color"
              value={selectedText.fill}
              onChange={(e) =>
                onTextUpdate(selectedText.id, { fill: e.target.value })
              }
              className="w-full h-8 border border-gray-300 rounded cursor-pointer"
            />
          </div>
          <div>
            <label
              className="block text-xs font-medium text-gray-700 mb-1"
              dir="rtl"
            >
              צבע מסגרת
            </label>
            <input
              type="color"
              value={selectedText.stroke}
              onChange={(e) =>
                onTextUpdate(selectedText.id, { stroke: e.target.value })
              }
              className="w-full h-8 border border-gray-300 rounded cursor-pointer"
            />
          </div>
        </div>

        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            עובי מסגרת: {selectedText.strokeWidth}px
          </label>
          <input
            type="range"
            min="0"
            max="10"
            step="1"
            value={selectedText.strokeWidth}
            onChange={(e) =>
              onTextUpdate(selectedText.id, {
                strokeWidth: parseInt(e.target.value),
              })
            }
            className="w-full h-1 bg-gray-200 rounded appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #ec4899 0%, #eab308 100%)`,
            }}
          />
        </div>

        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            רוחב טקסט:{" "}
            {selectedText.width ? Math.round(selectedText.width * 100) : 0}%
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={selectedText.width || 0}
            onChange={(e) =>
              onTextUpdate(selectedText.id, {
                width: parseFloat(e.target.value),
              })
            }
            className="w-full h-1 bg-gray-200 rounded appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #ec4899 0%, #eab308 100%)`,
            }}
          />
        </div>

        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            יישור טקסט
          </label>
          <div className="flex gap-1">
            <button
              dir="rtl"
              onClick={() => onTextUpdate(selectedText.id, { align: "right" })}
              className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                (selectedText.align || "right") === "right"
                  ? "bg-pink-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              ימין
            </button>
            <button
              dir="rtl"
              onClick={() => onTextUpdate(selectedText.id, { align: "center" })}
              className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                selectedText.align === "center"
                  ? "bg-pink-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              מרכז
            </button>
            <button
              dir="rtl"
              onClick={() => onTextUpdate(selectedText.id, { align: "left" })}
              className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                selectedText.align === "left"
                  ? "bg-pink-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              שמאל
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextEditingControls;
